import { ReactCodeMirrorRef } from "@uiw/react-codemirror"
import TextCodeFieldInput from "app/codemirror/components/TextCodeField"
import { AutocompleteFunction } from "app/codemirror/types"
import { FC, FocusEventHandler, forwardRef, PropsWithoutRef, Ref, useEffect, useRef } from "react"
import Textarea from "react-expanding-textarea"
import { FieldInputProps } from "react-final-form"
import tw from "tailwind-styled-components"
import { DisplayBehavior, ToolTipType, STooltip, ToolTipWrapper, SInputV2 } from "@superwall-me/sui"
import { useEnhancedField } from "./DisabledContext"

export enum LabeledTextFieldType {
  NORMAL,
  MULTILINE,
  CODE,
}

export interface LabeledTextFieldProps extends PropsWithoutRef<JSX.IntrinsicElements["input"]> {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  submitOnEnter?: boolean
  /* Tooltip text */
  helpText?: string
  /* Warning Tooltip text */
  warningText?: string
  /** Field type. Doesn't include radio buttons and checkboxes */
  type?: "text" | "password" | "email" | "number"
  outerProps?: PropsWithoutRef<JSX.IntrinsicElements["div"]>
  nullOnEmpty?: boolean
  /** Type of the Input */
  inputType?: LabeledTextFieldType
  /** A function to help with autocompletes for Code Type Inputs **/
  autoCompeteFunction?: AutocompleteFunction
  disabled?: boolean
  /** Use this to make the label flush with the input (aka no padding-left) */
  isFlushLabel?: boolean
  toolTipWidth?: string
  value?: string
  isBoldWhiteLabel?: boolean
}

export const FieldLabel = tw.label<{ $isError?: boolean }>`
  ${({ $isError }) => {
    const textStyle = $isError ? "text-red-800" : "text-white text-opacity-60"
    return `
      block
      pl-1
      ${textStyle}
      text-sm
    `
  }}
`

const TextAreaStyle = `
appearance-none
rounded-lg
mb-3
w-full
py-2
px-3
text-white
bg-grayscale-light
focus:outline-none
border-0
`

const LabeledOrValueTextFieldInputElement = forwardRef<
  HTMLInputElement | ReactCodeMirrorRef,
  {
    inputType: LabeledTextFieldType
    shouldDisable: boolean
    placeholderFromField?: string
    submitOnEnter?: boolean
    placeholderInput?: FieldInputProps<any, HTMLElement>
    autoCompeteFunction?: AutocompleteFunction
    disabled: boolean
    input: FieldInputProps<any, HTMLElement>
    props: Partial<LabeledTextFieldProps>
    label?: string
    prefix?: string
    onFocus?: () => void
    onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  }
>(
  (
    {
      inputType,
      input,
      disabled,
      label,
      props,
      autoCompeteFunction,
      placeholderFromField,
      placeholderInput,
      shouldDisable,
      prefix,
      onFocus,
      onBlur,
      submitOnEnter = true,
    },
    ref
  ) => {
    switch (inputType) {
      case LabeledTextFieldType.MULTILINE:
        return (
          //@ts-ignore
          <Textarea
            style={{
              outlineWidth: 0,
              outline: "none",
              boxShadow: "none",
            }}
            className={
              (shouldDisable ? "text-grayscale-lightest cursor-not-allowed" : "") +
              " " +
              TextAreaStyle
            }
            disabled={disabled}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            rows={3} // Mininum # of rows to set min-height
            ref={ref}
            /**
             * Not going to spread `input` to ensure that this component
             * doesn't get unnecessary props. Things like `onBlur`, `onChange`,
             * `onFocus`, etc. should be explicitly passed.
             */
            onChange={input.onChange}
            value={input.value}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-label={label}
          />
        )
      case LabeledTextFieldType.CODE: {
        return (
          <TextCodeFieldInput
            input={input}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            autoCompeteFunction={autoCompeteFunction!}
            ref={ref as Ref<ReactCodeMirrorRef>}
            aria-label={label}
            prefix={prefix}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        )
      }
      case LabeledTextFieldType.NORMAL:
      default: {
        return (
          <SInputV2
            className={
              shouldDisable
                ? "text-grayscale-lightest cursor-not-allowed !h-[45px]"
                : "!h-[38px] !bg-transparent relative"
            }
            disabled={disabled}
            {...props}
            {...(placeholderFromField ? { placeholder: placeholderInput!.value } : {})}
            ref={ref as Ref<HTMLInputElement>}
            aria-label={label}
            {...input}
            // onInput={(e) => {
            //   console.log("[!] onInput detected", e.currentTarget.value)
            //   input.onChange(e.currentTarget.value)
            // }}
            onAnimationStart={(e) => {
              console.log("[!] autofill detected")
              if (e.animationName === "onAutoFillStart") {
                // trigger Final Form’s change handler
                input.onChange(e.currentTarget.value)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitOnEnter) {
                e.preventDefault()
              }
            }}
            /**
             * If the below `onFocus` props is needed then be sure to
             * comment out the `{...input}` prop spread and explicitly
             * set the `value`, `onChange` and `onFocus` props.
             */
            // onFocus={onFocus} // <- uncomment this if ever needed
          />
        )
      }
    }
  }
)

LabeledOrValueTextFieldInputElement.displayName = "LabeledOrValueTextFieldInputElement"

export const LabeledTextFieldV2 = forwardRef<HTMLInputElement, LabeledTextFieldProps>(
  (
    {
      inputType = LabeledTextFieldType.NORMAL,
      submitOnEnter = true,
      warningText,
      helpText,
      disabled,
      name,
      label,
      className,
      outerProps,
      autoCompeteFunction,
      nullOnEmpty = false,
      isFlushLabel = false,
      toolTipWidth,
      value,
      isBoldWhiteLabel = false,
      ...props
    },
    ref
  ) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
      extra: { isDisabled },
      ...rest
    } = useEnhancedField(name, {
      parse: (value) => {
        if (props.type === "number") {
          return Number(value)
        }
        if (nullOnEmpty) {
          return value === "" ? null : value
        }
        return value
      },
      allowNull: nullOnEmpty,
    })

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
    // const normalizedError = Array.isArray(error) ? error.join(", ") : "Invalid"

    const shouldDisable = !!(isDisabled || disabled)

    useEffect(() => {
      if (!!value && input.value !== value) {
        input.onChange(value)
      }
    }, [input, value])

    return (
      <div className={`relative ${className || ""}`} {...outerProps}>
        <FieldLabel htmlFor={name}>
          <div className="flex flex-row  items-center">
            {helpText ? (
              <div className="relative -translate-y-1">
                <STooltip content={helpText} width="240px" placement="top-start">
                  <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
                </STooltip>
              </div>
            ) : (
              <span className={isBoldWhiteLabel ? "text-white font-bold" : ""}>{label}</span>
            )}
            {warningText ? (
              <div className="relative -translate-y-1">
                <STooltip content={warningText} type="warning" width="240px" placement="top-start">
                  <span>{label}</span>
                </STooltip>
              </div>
            ) : null}
          </div>
        </FieldLabel>
        <div className="relative mt-2">
          <LabeledOrValueTextFieldInputElement
            inputType={inputType}
            shouldDisable={shouldDisable}
            disabled={submitting || shouldDisable}
            autoCompeteFunction={autoCompeteFunction}
            input={input}
            label={label}
            props={props}
            submitOnEnter={submitOnEnter}
            ref={ref}
          />
          {touched && normalizedError && (
            <FormErrorV2 hasLabel={label === "" ? false : true} normalizedError={normalizedError} />
          )}
        </div>
      </div>
    )
  }
)

LabeledTextFieldV2.displayName = "LabeledTextFieldV2"

export const FormErrorV2: FC<{ normalizedError: string; hasLabel: boolean; isDropdown?: boolean }> =
  ({ normalizedError, hasLabel, isDropdown }) => {
    const rightPosition = isDropdown ? "right-6" : "right-1"
    return (
      <ToolTipWrapper
        role="alert"
        className={`
          appearance-none
          absolute
          mr-3 ${rightPosition} top-1/2 -translate-y-1/2`}
        displayBehavior={DisplayBehavior.Hover}
        toolTip={{
          state: ToolTipType.Standard,
          value: <p className="text-sm">{normalizedError}</p>,
        }}
      >
        {/* <FiAlertTriangle className="text-alert-primary" /> */}
        <svg
          className="text-amber-500/90"
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
        >
          <title>warning-sign</title>
          <g fill="currentColor">
            <path
              d="M23.641,18.485L14.732,1.643C13.931,.134,12.059-.44,10.55,.361c-.546,.29-.992,.736-1.282,1.282L.359,18.485c-.793,1.504-.217,3.367,1.288,4.16,.445,.235,.942,.357,1.445,.355H20.908c1.7,.005,3.083-1.369,3.088-3.07,.002-.504-.12-1-.355-1.445Zm-11.641,1.515c-.828,0-1.5-.672-1.5-1.5s.672-1.5,1.5-1.5,1.5,.672,1.5,1.5-.672,1.5-1.5,1.5Zm.53-5h-1.061c-.264,0-.483-.205-.499-.469l-.438-7c-.018-.288,.211-.531,.499-.531h1.936c.288,0,.517,.243,.499,.531l-.438,7c-.016,.264-.235,.469-.499,.469Z"
              fill="currentColor"
            ></path>
          </g>
        </svg>
      </ToolTipWrapper>
    )
  }

export default LabeledTextFieldV2
